import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { apolloHooks, ApolloMiddleware } from '@nxte-nl/apollo-hooks';

import { ACCOUNT_ROUTES } from '../../constants/account';
import { userLoginMutation } from '../../mutations/userLogin.gql';
import { userLogoutMutation } from '../../mutations/userLogout.gql';
import { ModalTypes } from '../../types/Modals';
import { BaseResponse } from '../../types/apiCompoundCommon';
import { MutationuserLogoutArgs, StatusResponse, UserResponse } from '../../types/apiTypes';
import { accountHooks } from '../accountHooks/accountHooks';
import { apolloMiddlewareHooks } from '../apolloMiddlewareHooks/apolloMiddlewareHooks';
import { dataLayerHooks } from '../dataLayerHooks/dataLayerHooks';
import { modalHooks } from '../modalHooks/modalHooks';
import { navigationHooks } from '../navigationHooks/navigationHooks';
import { routerBuildHooks } from '../routerBuildHooks/routerBuildHooks';
import { routerHooks } from '../routerHooks/routerHooks';

type UseLoginHandlersResult = {
  handleBackButton: () => void;
  handleCloseModal: () => void;
  isModalOpen: boolean;
};

type UseLoginUserParams = {
  onLoginSuccess?: (userResponse: UserResponse) => void;
};

export type UserLoginMutationParams = {
  captchaToken: string;
  email: string;
  password: string;
};

export type UseLoginMutationParams = {
  handleErrorCodeMiddleware: ApolloMiddleware;
  handleResponse: (response: BaseResponse<StatusResponse> | null) => void;
};

export const accountLoginHooks = {
  useHandleLoginUserResponseCallback(handleSetAccountUserInfo: () => void) {
    const loginUserSuccessCallback = dataLayerHooks.useLoginUserSuccessCallback();
    return React.useCallback((response: BaseResponse<StatusResponse> | null) => {
      if (response === null || !response.data) {
        return;
      }

      if (response.data.status) {
        loginUserSuccessCallback();
        handleSetAccountUserInfo();
      }
    }, [handleSetAccountUserInfo, loginUserSuccessCallback]);
  },
  useLoginMutation({
    handleResponse,
    handleErrorCodeMiddleware,
  }: UseLoginMutationParams) {
    const [executeRequest, { loading }] = apolloHooks.
      useMutation<BaseResponse<StatusResponse>, UserLoginMutationParams>(
        userLoginMutation, {
          onCompleted: handleResponse,
          context: {
            middleware: handleErrorCodeMiddleware,
          },
        },
      );

    return {
      executeRequest,
      loading,
    };
  },
  useLoginUser({
    onLoginSuccess,
  }: UseLoginUserParams) {
    const { executeRecaptcha } = useGoogleReCaptcha();

    const history = useHistory();

    const accountOverviewRedirect = navigationHooks.useAccountOverviewRedirect();

    const handleLoginSuccess = React.useCallback(() => {
      history.push(accountOverviewRedirect);
    }, [history, accountOverviewRedirect]);

    const {
      handleSetAccountUserInfo,
      userInfoLoading,
    } = accountHooks.useSetAccountUserInfo(onLoginSuccess || handleLoginSuccess);

    const {
      error,
      handleErrorCodeMiddleware,
      resetApiError,
    } = apolloMiddlewareHooks.useErrorCodeMiddleware();

    const handleResponse = accountLoginHooks
      .useHandleLoginUserResponseCallback(handleSetAccountUserInfo);

    const {
      executeRequest,
      loading,
    } = accountLoginHooks.useLoginMutation({
      handleErrorCodeMiddleware,
      handleResponse,
    });

    const handleLoginUser = React.useCallback(async (email: string, password: string) => {
      if (executeRecaptcha) {
        const captchaToken = await executeRecaptcha(
          CONFIG.reCaptchaBalanceCheckAction,
        );

        const params = captchaToken && email && password
          ? {
            captchaToken,
            email,
            password,
          }
          : null;

        if (params) {
          resetApiError();
          executeRequest({
            variables: params,
          });
        }
      }
    }, [executeRecaptcha, executeRequest, resetApiError]);

    return {
      loading: loading || userInfoLoading,
      handleLoginUser,
      handleResponse,
      handleLoginSuccess,
      apiError: error ?? null,
    };
  },
  useHandleResponseLogoutUserCallback(successCallback?: () => void) {
    const pathname = routerHooks.usePathName();
    const intl = useIntl();
    const history = useHistory();
    const doesPathMatchRoutesHandler = accountHooks.useIsPathToRouteMatchedHandler();

    const resetAccount = accountHooks.useResetAccount();
    const homePageRedirect = navigationHooks.useHomePageRedirect();
    const handleResponse = React.useCallback((response: BaseResponse<StatusResponse> | null) => {
      if (response === null || !response.data || !response.data.status) {
        return;
      }

      const accountRoutes = ACCOUNT_ROUTES.map((route) => intl.formatMessage(route));

      const isMatch = doesPathMatchRoutesHandler(pathname, accountRoutes);

      if (isMatch) {
        history.push(homePageRedirect());
      }

      successCallback && successCallback();
      resetAccount();
    }, [
      doesPathMatchRoutesHandler,
      pathname,
      successCallback,
      resetAccount,
      intl,
      history,
      homePageRedirect,
    ]);
    return { handleResponse };
  },
  useLogoutMutation(handleResponse: (response: BaseResponse<StatusResponse> | null) => void) {
    const [executeRequest, { loading }] = apolloHooks.useMutation<
    BaseResponse<StatusResponse>, Omit<MutationuserLogoutArgs, 'userData'>>(
      userLogoutMutation,
      { onCompleted: handleResponse },
    );
    return {
      executeRequest,
      loading,
    };
  },
  useLogoutUser(successCallback?: () => void) {
    const { handleResponse } = accountLoginHooks.
      useHandleResponseLogoutUserCallback(successCallback);

    const {
      executeRequest,
      loading,
    } = accountLoginHooks.useLogoutMutation(handleResponse);

    const handleLogoutUser = React.useCallback(() => {
      executeRequest({});
    }, [executeRequest]);

    return {
      loading,
      handleResponse,
      handleLogoutUser,
    };
  },
  useLoginHandlers(): UseLoginHandlersResult {
    const { onModalClose, isOpenModal } = modalHooks.useModal(ModalTypes.LoginModal);

    const handleBackButton = routerBuildHooks.useGoBack();

    const handleCloseModal = React.useCallback(() => {
      onModalClose();
    }, [onModalClose]);

    const isModalOpen = React.useMemo(
      () => isOpenModal(),
      [isOpenModal],
    );

    return {
      isModalOpen,
      handleCloseModal,
      handleBackButton,
    };
  },
};
